import { ChangeEvent, useMemo, useState } from 'react'

import {
  Button,
  Checkbox,
  Modal,
  ModalProvider,
  SecondaryButton,
  SemanticIcon,
  SquareDotLoadingIndicator,
  TabPanel,
  TabularMenu,
  TabularMenuProvider,
  typography,
} from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import { BillingAddress, ShippingAddress } from '__generated__/atlas-types'

import usePortal from 'hooks/usePortal'
import { isAccountTypeBrand as isAccountTypeBrandSelector } from 'store/currentUser/selectors'

import { StepsModalPayload } from '../../AddressesWidget'
import {
  AddressesSubmitParams,
  OrderBillingShippingData,
  SubmitParams,
} from '../../addressesWidget.hooks'
import { BillingAddressForm } from '../Forms/BillingAddressForm/BillingAddressForm'
import {
  BillingAddressFormValues,
  FieldIds,
} from '../Forms/BillingAddressForm/billingAddressForm.types'
import { ShippingAddressForm } from '../Forms/ShippingAddressForm/ShippingAddressForm'
import { ShippingAddressFormValues } from '../Forms/ShippingAddressForm/shippingAddressForm.types'
import { AddressModalData } from './hooks/useAddressModalData'
import { useBillingForm } from './hooks/useBillingForm'
import { useBillingOptions } from './hooks/useBillingOptions'
import {
  ModalTab,
  useShippingBillingTabs,
} from './hooks/useShippingBillingTabs'
import { useShippingForm } from './hooks/useShippingForm'
import { useShippingOptions } from './hooks/useShippingOptions'
import { AddressReviewPageIds } from 'features/Orders/Review/review.ids'

const CHECKBOX_LABELS = ['Billing', 'Shipping'] as const

export type Props = {
  modalData: AddressModalData
  orderData: OrderData
  isSubmitting?: boolean
  onSubmitWithWarning: (params: AddressesSubmitParams) => void
  onSubmitWithMismatch: (
    params: AddressesSubmitParams,
    csdSelectedAddress?: ShippingAddress | BillingAddress,
  ) => void
  onSubmitWithoutWarning: (params: AddressesSubmitParams) => void
  onSubmitWithStepsModal: (payload: StepsModalPayload) => void
  onClose: () => void
  readonly: boolean
}

const TestIds = {
  SubmitButton: 'edit-addresses-modal-submit-button',
} as const

export function EditAddressesModal({
  modalData,
  orderData,
  isSubmitting,
  onSubmitWithWarning,
  onSubmitWithMismatch,
  onSubmitWithoutWarning,
  onSubmitWithStepsModal,
  onClose,
  readonly,
}: Props) {
  const Portal = usePortal()
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)
  const isAccountTypeRetailer = !isAccountTypeBrand
  const [
    isSameAddressForBillingAndShipping,
    setIsSameAddressForBillingAndShipping,
  ] = useState(false)

  const { tabIndex, toggleTab, activeTab } = useShippingBillingTabs()

  const {
    door,
    retailerId,
    orderStatus,
    isOrderOrphan,
    trackingNumber,
    displayShippingCode,
    orderShippingMethod,
    paymentMethod,
    isRetailerRestrictedFromEditing,
  } = orderData

  const {
    orderTax,
    countries,
    doorOptions,
    orderBilling,
    orderShipping,
    billingAddresses,
    shippingAddresses,
    retailerInfo,
    paymentMethodOptions,
    shippingMethodOptions,
    loadingAddressModalData,
  } = modalData

  // COUNTRY DROPDOWN OPTIONS are the same for both shipping and billing
  const countryOptions = useMemo(
    () =>
      countries?.map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [countries],
  )

  // SHIPPING DROPDOWN OPTIONS
  const { shippingAddressesOptionGroup, aggregatedShippingOptions } =
    useShippingOptions({
      orderShipping,
      shippingAddresses,
      retailerInfo,
    })

  // SHIPPING FORM VALUES / HANDLERS
  const {
    shippingValues,
    shippingErrors,
    shippingNameLabel,
    resetShippingForm,
    isValidShippingForm,
    shippingDoorHandlers,
    handleShippingChange,
    isShippingFormTouched,
    shippingMethodHandlers,
    shippingCountryHandlers,
    isPrimaryLocShippingSet,
    selectedCSDShippingAddress,
    disableShippingCompanyField,
    handleSelectShippingAddress,
    hasShippingCSDMismatch,
  } = useShippingForm({
    orderDoor: door,
    doorOptions,
    orderShipping,
    countryOptions,
    trackingNumber,
    retailerInfo,
    shippingAddresses,
    aggregatedOptions: aggregatedShippingOptions,
    shippingMethodOptions,
    orderShippingMethodId: orderShippingMethod?.id,
  })

  // BILLING DROPDOWN OPTIONS
  const { aggregatedBillingOptions, billingAddressesOptionGroup } =
    useBillingOptions({
      billingAddresses,
      retailerLocations: retailerInfo?.locations,
      primaryLocation: retailerInfo?.primaryLocation,
      orderBilling,
    })

  // BILLING FORM VALUES / HANDLERS
  const {
    billingValues,
    billingErrors,
    billingTaxLabel,
    billingNameLabel,
    resetBillingForm,
    isValidBillingForm,
    handleBillingChange,
    isBillingFormTouched,
    billingCountryHandlers,
    isPrimaryLocBillingSet,
    selectedCSDBillingAddress,
    handleSelectBillingAddress,
    disableBillingCompanyField,
    billingPaymentMethodHandlers,
    hasBillingCSDMismatch,
  } = useBillingForm({
    orderBilling,
    orderTax,
    paymentMethod,
    countryOptions,
    billingAddresses,
    aggregatedOptions: aggregatedBillingOptions,
    retailerInfo,
    countries,
  })

  const isAnyFormTouched = isShippingFormTouched || isBillingFormTouched
  const areFormsValid = isValidShippingForm && isValidBillingForm

  /** HANDLERS */
  const handleTabClick = () => {
    toggleTab()
    setIsSameAddressForBillingAndShipping(false)
  }

  const submitSingleAddress = (
    values: ShippingAddressFormValues | BillingAddressFormValues,
    fromTab: ModalTab,
  ) => {
    const changesMade = isShippingFormTouched || isBillingFormTouched

    const params: SubmitParams = {
      values,
      overwriteAddress: isSameAddressForBillingAndShipping,
      changesMade,
      initialDoor: door,
      initialTrackingNumber: trackingNumber,
    }

    const key: keyof AddressesSubmitParams =
      fromTab === 'Shipping' ? 'shipping' : 'billing'

    const addressesSubmitParams = { [key]: params }

    const selectedAddressOption = values[FieldIds.AddressOption]

    const isPrimaryLocationSet =
      retailerInfo?.primaryLocation?.id === selectedAddressOption?.value

    const hasToOpenWarningModal =
      isAccountTypeRetailer ||
      (isAccountTypeBrand && (isOrderOrphan || isPrimaryLocationSet))

    if (hasToOpenWarningModal) {
      onSubmitWithWarning(addressesSubmitParams)
      return
    }

    const csdSelectedAddress = isShippingFormTouched
      ? selectedCSDShippingAddress
      : selectedCSDBillingAddress

    const hasMismatch = isShippingFormTouched
      ? hasShippingCSDMismatch
      : hasBillingCSDMismatch

    const hasToOpenMismatchModal =
      isAccountTypeBrand && Boolean(csdSelectedAddress) && hasMismatch

    if (hasToOpenMismatchModal) {
      onSubmitWithMismatch(addressesSubmitParams, csdSelectedAddress)
      return
    }

    onSubmitWithoutWarning(addressesSubmitParams)
  }

  const submitBothAddresses = () => {
    // keep in mind we are here bc both forms are touched
    // The isSameAddressForBillingAndShipping always has to be false so:
    const overwriteAddress = false

    const addressesSubmitParams: AddressesSubmitParams = {
      shipping: {
        values: shippingValues,
        overwriteAddress,
        changesMade: true,
        initialDoor: door,
        initialTrackingNumber: trackingNumber,
      },
      billing: {
        values: billingValues,
        overwriteAddress,
        changesMade: true,
      },
    }

    // RETAILER ACCOUNT always has to open the -WARNING MODAL-
    // bc always updates at order level
    if (isAccountTypeRetailer) {
      onSubmitWithWarning(addressesSubmitParams)
      return
    }

    // BRAND ACCOUNT -WARNING MODAL-
    // ==============================
    // Brand Account has to open the warning modal
    // if the order is orphan and both addresses are primary locations
    if (isOrderOrphan || (isPrimaryLocBillingSet && isPrimaryLocShippingSet)) {
      onSubmitWithWarning(addressesSubmitParams)
      return
    }

    // Brand Account has to open the warning modal when:
    // One of the addresses is a primary location but the other
    // is an order address or a CSD not modified address

    // SHIPPING conditions
    const isShipping_A_CSDModifiedAddress =
      !!selectedCSDShippingAddress && hasShippingCSDMismatch

    const isShipping_A_CSD_Not_ModifiedAddress =
      !!selectedCSDShippingAddress && !hasShippingCSDMismatch

    const isShipping_An_OrderAddress =
      !isPrimaryLocShippingSet && !selectedCSDShippingAddress

    // BILLING conditions
    const isBilling_A_CSDModifiedAddress =
      !!selectedCSDBillingAddress && hasBillingCSDMismatch

    const isBilling_A_CSD_Not_ModifiedAddress =
      !!selectedCSDBillingAddress && !hasBillingCSDMismatch

    const isBilling_An_OrderAddress =
      !isPrimaryLocBillingSet && !selectedCSDBillingAddress

    // 1. when shipping is primary location and billing is not
    //    primary location or is a CSD not modified address or
    //    is not a CSD address
    if (
      isPrimaryLocShippingSet &&
      (isBilling_A_CSD_Not_ModifiedAddress || isBilling_An_OrderAddress)
    ) {
      onSubmitWithWarning(addressesSubmitParams)
      return
    }

    // 2. when billing is primary location and shipping is not
    //    primary location or is a CSD not modified address or
    //    is not a CSD address
    if (
      isPrimaryLocBillingSet &&
      (isShipping_A_CSD_Not_ModifiedAddress || isShipping_An_OrderAddress)
    ) {
      onSubmitWithWarning(addressesSubmitParams)
      return
    }

    // BRAND ACCOUNT -MISMATCH MODAL-
    // ==============================
    // Brand Account has to open the mismatch modal when:
    // 1. when shipping is a CSD address and has mismatch and
    //    billing is CSD address not modified or is not a
    //    primary location or is not a CSD address
    if (
      isShipping_A_CSDModifiedAddress &&
      (isBilling_A_CSD_Not_ModifiedAddress || isBilling_An_OrderAddress)
    ) {
      onSubmitWithMismatch(addressesSubmitParams, selectedCSDShippingAddress)
      return
    }

    // 2. when billing is a CSD address and has mismatch and
    //    shipping is CSD address not modified or is not a
    //    primary location or is not a CSD address
    if (
      isBilling_A_CSDModifiedAddress &&
      (isShipping_A_CSD_Not_ModifiedAddress || isShipping_An_OrderAddress)
    ) {
      onSubmitWithMismatch(addressesSubmitParams, selectedCSDBillingAddress)
      return
    }

    // BRAND ACCOUNT -STEPS MODAL-
    // ==============================
    // Brand Account has to open the steps modal when:
    // 1. the two addresses are csd and have mismatch
    if (isShipping_A_CSDModifiedAddress && isBilling_A_CSDModifiedAddress) {
      onSubmitWithStepsModal({
        params: addressesSubmitParams,
        types: {
          shipping: 'csd',
          billing: 'csd',
        },
        csdAddresses: {
          shipping: selectedCSDShippingAddress,
          billing: selectedCSDBillingAddress,
        },
      })
      return
    }

    // 2. Shipping address is csd and has mismatch and billing address is primary location
    if (isShipping_A_CSDModifiedAddress && isPrimaryLocBillingSet) {
      onSubmitWithStepsModal({
        params: addressesSubmitParams,
        types: {
          shipping: 'csd',
          billing: 'local',
        },
        csdAddresses: {
          shipping: selectedCSDShippingAddress,
          billing: undefined,
        },
      })
      return
    }

    // 3. Billing address is csd and has mismatch and shipping address is primary location
    if (isBilling_A_CSDModifiedAddress && isPrimaryLocShippingSet) {
      onSubmitWithStepsModal({
        params: addressesSubmitParams,
        types: {
          shipping: 'local',
          billing: 'csd',
        },
        csdAddresses: {
          shipping: undefined,
          billing: selectedCSDBillingAddress,
        },
      })
      return
    }

    onSubmitWithoutWarning(addressesSubmitParams)
  }

  const handleSubmit = () => {
    // In case isSameAddressForBillingAndShipping and no editions
    // the form is going always to be submitted as a single address
    // per flow the user can only set the checkbox in the same tab
    // if the user changes tab, the check is set to false
    if (isSameAddressForBillingAndShipping) {
      // Then we have to select the values for the payload
      // depending on which tab is active
      const values = activeTab === 'Shipping' ? shippingValues : billingValues
      submitSingleAddress(values, activeTab)
      return
    }

    const isJustOneFormTouched = isShippingFormTouched !== isBillingFormTouched

    if (isJustOneFormTouched) {
      const values = isShippingFormTouched ? shippingValues : billingValues
      // we have to check this bc the user can:
      // 1. edit a form
      // 2. change tab
      // 3. submit without touching the just shown form
      // so we cannot relay on the activeTab here
      const fromTab = isShippingFormTouched ? 'Shipping' : 'Billing'
      submitSingleAddress(values, fromTab)
      return
    }

    // After the above checks, we can assume that both forms are touched
    submitBothAddresses()
  }

  return (
    <Portal>
      <ModalProvider>
        <Modal
          title={{ text: 'Edit Addresses', iconName: 'edit' }}
          isOpen
          onClose={onClose}
          disableBackgroundClick
        >
          {loadingAddressModalData ? (
            <LoaderContainer>
              <SquareDotLoadingIndicator />
            </LoaderContainer>
          ) : (
            <Modal.Content>
              <TabularMenuProvider
                index={tabIndex}
                tabs={[
                  {
                    id: 'shipping',
                    label: (
                      <TabLabelHolder>
                        {isValidShippingForm && isShippingFormTouched && (
                          <ModifiedDot />
                        )}
                        <span className={!isValidShippingForm ? 'error' : ''}>
                          Shipping Info
                        </span>
                        {!isValidShippingForm && (
                          <SemanticIcon iconName="error" iconSize="small" />
                        )}
                      </TabLabelHolder>
                    ),
                    onClick: handleTabClick,
                    component: (
                      <ShippingAddressForm
                        values={shippingValues}
                        errors={shippingErrors}
                        doorOptions={doorOptions}
                        orderStatus={orderStatus}
                        resetForm={resetShippingForm}
                        countryOptions={countryOptions}
                        formId={'shipping-address-form'}
                        handleChange={handleShippingChange}
                        shippingDoorHandlers={shippingDoorHandlers}
                        addressesOptionGroup={shippingAddressesOptionGroup}
                        shippingMethodOptions={shippingMethodOptions}
                        aggregatedOptions={aggregatedShippingOptions}
                        onSelectAddress={handleSelectShippingAddress}
                        shippingMethodHandlers={shippingMethodHandlers}
                        disableEditCompany={disableShippingCompanyField}
                        shippingCountryHandlers={shippingCountryHandlers}
                        showShippingCode={
                          displayShippingCode && isAccountTypeBrand
                        }
                        retailerId={retailerId}
                        isRetailerRestrictedFromCreating={
                          isRetailerRestrictedFromEditing
                        }
                        companyNameLabel={shippingNameLabel}
                        readonly={readonly}
                      />
                    ),
                  },
                  {
                    id: 'billing',
                    label: (
                      <TabLabelHolder>
                        {isValidBillingForm && isBillingFormTouched && (
                          <ModifiedDot />
                        )}
                        <span className={!isValidBillingForm ? 'error' : ''}>
                          Billing Info
                        </span>
                        {!isValidBillingForm && (
                          <SemanticIcon iconName="error" iconSize="small" />
                        )}
                      </TabLabelHolder>
                    ),
                    onClick: handleTabClick,
                    component: (
                      <BillingAddressForm
                        formId={'billing-address-form'}
                        paymentMethodOptions={paymentMethodOptions}
                        orderStatus={orderStatus}
                        addressesOptionGroup={billingAddressesOptionGroup}
                        aggregatedOptions={aggregatedBillingOptions}
                        onSelectAddress={handleSelectBillingAddress}
                        disableEditCompany={disableBillingCompanyField}
                        countryOptions={countryOptions}
                        currentTaxLabel={billingTaxLabel}
                        countryHandlers={billingCountryHandlers}
                        paymentMethodHandlers={billingPaymentMethodHandlers}
                        handleChange={handleBillingChange}
                        values={billingValues}
                        errors={billingErrors}
                        resetForm={resetBillingForm}
                        retailerId={retailerId}
                        isRetailerRestrictedFromCreating={
                          isRetailerRestrictedFromEditing
                        }
                        companyNameLabel={billingNameLabel}
                        readonly={readonly}
                      />
                    ),
                  },
                ]}
              >
                <TabsWrapper>
                  <TabularMenu />
                  <TabPanel />
                </TabsWrapper>
              </TabularMenuProvider>
            </Modal.Content>
          )}
          <Modal.Actions>
            <ActionsWrapper>
              <div>
                <StyledCheckbox
                  id={
                    tabIndex
                      ? AddressReviewPageIds.SameAddressForShippingId
                      : AddressReviewPageIds.SameAddressForBillingId
                  }
                  label={`Use as ${CHECKBOX_LABELS[tabIndex]} Address`}
                  checked={isSameAddressForBillingAndShipping}
                  onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                    setIsSameAddressForBillingAndShipping(event.target.checked)
                  }}
                />
                {isSameAddressForBillingAndShipping && (
                  <CheckBoxCaption>
                    This will only apply to and save in this order.
                  </CheckBoxCaption>
                )}
              </div>
              <ActionsButtons>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <Button
                  disabled={
                    !areFormsValid ||
                    (!isAnyFormTouched && !isSameAddressForBillingAndShipping)
                  }
                  loading={isSubmitting}
                  onClick={handleSubmit}
                  data-testid={TestIds.SubmitButton}
                >
                  Update Address
                </Button>
              </ActionsButtons>
            </ActionsWrapper>
          </Modal.Actions>
        </Modal>
      </ModalProvider>
    </Portal>
  )
}

export type OrderData = Pick<
  OrderBillingShippingData,
  | 'orderStatus'
  | 'retailerId'
  | 'displayShippingCode'
  | 'door'
  | 'orderShippingMethod'
  | 'trackingNumber'
  | 'paymentMethod'
  | 'isOrderOrphan'
  | 'isRetailerRestrictedFromEditing'
>

const StyledCheckbox = styled(Checkbox)`
  ${typography.alpha.primaryActive.body3}
`

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ActionsButtons = styled.div`
  display: flex;
  gap: 16px;
`

const CheckBoxCaption = styled.div`
  color: var(--Primary-900, #2d3439);
  font-family: 'Nunito Sans';
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 1px;
  margin-left: 20px;
`

const LoaderContainer = styled.div`
  width: 670px;
  height: 375px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const TabsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const TabLabelHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .error {
    color: var(--color-error-500);
  }
`

const ModifiedDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: var(--color-secondary-600);
`
